"use client";

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { subMonths } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { usePostAggregatePizzas } from "@/api/pizza-checker/pizzas/pizzas";
import { useDashboardDateRange } from "@/context/dashboard-date-range-context";
import { useBranchFilter } from "@/hooks/use-branch-filter";
import { usePermissions } from "@/hooks/use-permissions";
import { useLocalization } from "@/context/localization-context";
import { getComponentErrorsLink } from "@/pizzas/component-errors";

interface ScoreByArticleProps {
  className?: string;
}

type ScoreMode = "top" | "bottom";

type ComponentScoreRow = {
  name: string;
  score: number;
  total: number;
  ok: number;
  nok: number;
  trend: number | null;
};

const readNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const componentEventsExpression = (path: string, isOk: boolean) => ({
  $map: {
    input: { $ifNull: [path, []] },
    as: "component",
    in: {
      name: "$$component",
      ok: isOk ? 1 : 0,
      nok: isOk ? 0 : 1,
    },
  },
});

const buildComponentScorePipeline = (
  start: Date,
  end: Date,
  branchFilter: Record<string, unknown>,
) => [
  {
    $match: {
      ...branchFilter,
      "evaluation.metadata.components_evaluation": { $exists: true },
      capture_at: {
        $gte: start.toISOString(),
        $lte: end.toISOString(),
      },
    },
  },
  {
    $project: {
      componentEvents: {
        $concatArrays: [
          componentEventsExpression(
            "$evaluation.metadata.components_evaluation.base_components.found",
            true,
          ),
          componentEventsExpression(
            "$evaluation.metadata.components_evaluation.base_components.missing",
            false,
          ),
          componentEventsExpression(
            "$evaluation.metadata.components_evaluation.extras.found",
            true,
          ),
          componentEventsExpression(
            "$evaluation.metadata.components_evaluation.extras.missing",
            false,
          ),
          componentEventsExpression(
            "$evaluation.metadata.components_evaluation.removals.not_expected",
            true,
          ),
          componentEventsExpression(
            "$evaluation.metadata.components_evaluation.removals.found",
            false,
          ),
        ],
      },
    },
  },
  { $unwind: "$componentEvents" },
  { $match: { "componentEvents.name": { $ne: null } } },
  {
    $group: {
      _id: "$componentEvents.name",
      total: { $sum: 1 },
      ok: { $sum: "$componentEvents.ok" },
      nok: { $sum: "$componentEvents.nok" },
    },
  },
  { $match: { total: { $gte: 5 } } },
  {
    $project: {
      _id: 0,
      name: "$_id",
      total: 1,
      ok: 1,
      nok: 1,
      score: {
        $cond: [
          { $gt: ["$total", 0] },
          { $multiply: [{ $divide: ["$ok", "$total"] }, 100] },
          0,
        ],
      },
    },
  },
];

const normalizeRows = (response: unknown) => {
  const results = (response as { results?: unknown[] }).results ?? [];

  return results
    .map((row) => {
      const result = row as Record<string, unknown>;
      const name = typeof result.name === "string" ? result.name : "";

      return {
        name,
        score: readNumber(result.score),
        total: readNumber(result.total),
        ok: readNumber(result.ok),
        nok: readNumber(result.nok),
      };
    })
    .filter((row) => row.name);
};

export function ScoreByArticle({ className }: ScoreByArticleProps) {
  const { processedDateRange } = useDashboardDateRange();
  const { start, end } = processedDateRange;
  const branchFilter = useBranchFilter();
  const { t, tp, formatNumber } = useLocalization();
  const { hasPermission } = usePermissions();
  const canOpenPizzas =
    hasPermission("pizzas") || hasPermission("pizzas.branch");

  const [mode, setMode] = useState<ScoreMode>("bottom");
  const [data, setData] = useState<ComponentScoreRow[]>([]);
  const { mutateAsync, isPending } = usePostAggregatePizzas({});

  /** a value in percent, rounded (58.4 -> "58%" / "58 %") */
  const formatPercent = (value: number) =>
    formatNumber(Math.round(value) / 100, { style: "percent" });

  const formatTrend = (trend: number | null) => {
    if (trend === null) {
      return t("componentsScore.noTrendData");
    }

    return `${trend >= 0 ? "+" : ""}${formatNumber(trend, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })} ${t("componentsScore.points")}`;
  };

  useEffect(() => {
    if (!start || !end) return;

    let isMounted = true;

    const fetchData = async () => {
      try {
        const previousStart = subMonths(start, 1);
        const previousEnd = subMonths(end, 1);

        const [currentResponse, previousResponse] = await Promise.all([
          mutateAsync({
            data: {
              pipeline: buildComponentScorePipeline(start, end, branchFilter),
            },
          }),
          mutateAsync({
            data: {
              pipeline: buildComponentScorePipeline(
                previousStart,
                previousEnd,
                branchFilter,
              ),
            },
          }),
        ]);

        if (!isMounted) return;

        const previousByName = new Map(
          normalizeRows(previousResponse).map((row) => [row.name, row]),
        );

        const formattedData = normalizeRows(currentResponse).map((row) => {
          const previous = previousByName.get(row.name);

          return {
            ...row,
            trend: previous ? row.score - previous.score : null,
          };
        });

        setData(formattedData);
      } catch (error) {
        console.error("Error fetching component scores:", error);
        if (isMounted) {
          setData([]);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [mutateAsync, start, end, branchFilter]);

  const displayData = useMemo(() => {
    return [...data]
      .sort((a, b) =>
        mode === "top" ? b.score - a.score : a.score - b.score,
      )
      .slice(0, 5);
  }, [data, mode]);

  return (
    <Card className={cn(className)}>
      <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-base font-medium uppercase tracking-wider text-muted-foreground">
          {t("componentsScore.title")}
        </CardTitle>
        <div className="flex rounded-md border p-0.5">
          <Button
            type="button"
            variant={mode === "bottom" ? "secondary" : "ghost"}
            size="sm"
            className="h-7 px-3 text-xs"
            onClick={() => setMode("bottom")}
          >
            {t("componentsScore.bottom5")}
          </Button>
          <Button
            type="button"
            variant={mode === "top" ? "secondary" : "ghost"}
            size="sm"
            className="h-7 px-3 text-xs"
            onClick={() => setMode("top")}
          >
            {t("componentsScore.top5")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            {t("common.loading")}
          </div>
        ) : displayData.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            {t("common.noPeriodData")}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {displayData.map((item) => {
              const errorsLink =
                canOpenPizzas && item.nok > 0 && start && end
                  ? getComponentErrorsLink(item.name, start, end)
                  : null;
              const rowClassName =
                "group -mx-2 grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 rounded-md px-2 py-1 text-sm";

              const content = (
                <>
                  <div className="min-w-0">
                    <div className="truncate font-medium" title={item.name}>
                      {item.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {tp("componentsScore.samples", item.total, {
                        count: formatNumber(item.total),
                      })}
                      ,{" "}
                      <span
                        className={cn(
                          errorsLink &&
                            "underline-offset-2 group-hover:text-foreground group-hover:underline",
                        )}
                      >
                        {tp("componentsScore.errors", item.nok, {
                          count: formatNumber(item.nok),
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-16 text-right text-base font-semibold tabular-nums">
                    {formatPercent(item.score)}
                  </div>
                  <Badge
                    variant="outline"
                    className={cn(
                      "justify-center tabular-nums",
                      item.trend === null
                        ? "text-muted-foreground"
                        : item.trend >= 0
                          ? "border-green-500/30 bg-green-500/15 text-green-400"
                          : "border-red-500/30 bg-red-500/15 text-red-400",
                    )}
                  >
                    {formatTrend(item.trend)}
                  </Badge>
                </>
              );

              return errorsLink ? (
                <Link
                  key={item.name}
                  to={errorsLink}
                  title={t("componentsScore.showErrors")}
                  className={cn(
                    rowClassName,
                    "transition-colors hover:bg-muted/60",
                  )}
                >
                  {content}
                </Link>
              ) : (
                <div key={item.name} className={rowClassName}>
                  {content}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
